import {
  Component,
  ElementRef,
  OnDestroy,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { Tag } from '@core/models/tags.model';
import { ICONS } from '@core/services/icons/icons.service';
import { selectTags } from '@core/store/admin/tags-labels/tags-labels.selectors';
import { foldForSearch } from '@core/utils/search-text.utils';

import { FormBuilderBase } from '@shared/components/row-edition-form/form-builder/form-builder.model';

@Component({
  selector: 'app-tags-input',
  imports: [FormsModule, TranslatePipe],
  templateUrl: './tags-input.component.html',
  styles: [
    `
      :host {
        display: flex;
        width: 100%;
      }
    `
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagsInputComponent),
      multi: true
    }
  ]
})
export class TagsInputComponent implements ControlValueAccessor, OnDestroy {
  private readonly store = inject(Store);
  protected readonly ICONS = ICONS;

  id = input<FormBuilderBase['id']>();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value = input<any>();

  protected readonly availableTags = toSignal(this.store.select(selectTags), {
    initialValue: [] as Tag[]
  });

  protected readonly tagsByLabel = computed(
    () => new Map(this.availableTags().map((t) => [t.label, t]))
  );

  protected selectedItems = signal<string[]>([]);
  protected searchText = signal('');
  protected isDropdownOpen = signal(false);
  protected highlightedIndex = signal(-1);

  protected readonly filteredOptions = computed(() => {
    const search = foldForSearch(this.searchText()).trim();
    const selected = new Set(this.selectedItems());
    return this.availableTags().filter(
      (t) => !selected.has(t.label) && foldForSearch(t.label).includes(search)
    );
  });

  private readonly referenceEl = viewChild<ElementRef<HTMLElement>>('reference');
  private readonly dropdownEl = viewChild<ElementRef<HTMLElement>>('dropdown');
  private cleanupAutoUpdate?: () => void;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private onChange: (_value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Position the dropdown as a fixed-strategy overlay so it escapes the
    // clipping (`overflow-y-auto`/`overflow-hidden`) of the surrounding
    // row-edition-form scroll container and form-builder sections (issue #1179).
    effect(() => {
      const open = this.isDropdownOpen();
      const reference = this.referenceEl()?.nativeElement;
      const dropdown = this.dropdownEl()?.nativeElement;

      this.cleanupAutoUpdate?.();
      this.cleanupAutoUpdate = undefined;

      if (!open || !reference || !dropdown) {
        return;
      }

      this.cleanupAutoUpdate = autoUpdate(reference, dropdown, () => {
        computePosition(reference, dropdown, {
          strategy: 'fixed',
          placement: 'bottom-start',
          middleware: [
            offset(4),
            flip(),
            shift({ padding: 8 }),
            size({
              apply: ({ rects, elements }) => {
                elements.floating.style.width = `${rects.reference.width}px`;
              }
            })
          ]
        }).then(({ x, y }) => {
          Object.assign(dropdown.style, {
            position: 'fixed',
            left: `${x}px`,
            top: `${y}px`
          });
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.cleanupAutoUpdate?.();
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchText.set(input.value);
    this.updateDropdown();
  }

  onKeyDown(event: KeyboardEvent): void {
    const options = this.filteredOptions();

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (options.length > 0) {
        this.highlightedIndex.set((this.highlightedIndex() + 1) % options.length);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (options.length > 0) {
        this.highlightedIndex.set(
          this.highlightedIndex() <= 0 ? options.length - 1 : this.highlightedIndex() - 1
        );
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const idx = this.highlightedIndex();
      if (idx >= 0 && idx < options.length) {
        this.selectTag(options[idx].label);
      } else if (options.length > 0) {
        this.selectTag(options[0].label);
      }
    } else if (event.key === 'Backspace' && this.searchText().length === 0) {
      const current = this.selectedItems();
      if (current.length > 0) {
        this.removeTag(current[current.length - 1]);
      }
    } else if (event.key === 'Escape') {
      this.isDropdownOpen.set(false);
      this.onTouched();
    }
  }

  onInputFocus(): void {
    this.updateDropdown();
  }

  onInputBlur(): void {
    setTimeout(() => {
      this.isDropdownOpen.set(false);
      this.onTouched();
    }, 200);
  }

  selectTag(label: string): void {
    if (!this.selectedItems().includes(label)) {
      this.selectedItems.set([...this.selectedItems(), label]);
      this.searchText.set('');
      this.isDropdownOpen.set(false);
      this.highlightedIndex.set(-1);
      this.notifyChanges();
    }
  }

  removeTag(label: string): void {
    this.selectedItems.set(this.selectedItems().filter((l) => l !== label));
    this.notifyChanges();
  }

  private updateDropdown(): void {
    const options = this.filteredOptions();
    this.isDropdownOpen.set(options.length > 0);
    this.highlightedIndex.set(options.length > 0 ? 0 : -1);
  }

  private notifyChanges(): void {
    this.onChange(this.selectedItems().length > 0 ? this.selectedItems() : null);
  }

  writeValue(value: string[] | null): void {
    this.selectedItems.set(Array.isArray(value) ? value : []);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
